/**
 * FinOps Copilot (Patent Sub-system S)
 * State-machine orchestrator across allocate→propose→approve→execute→verify.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2, FlaskConical, Workflow as WorkflowIcon, CheckCircle2, AlertOctagon, Clock } from 'lucide-react';
import DemoDataBanner from '../components/DemoDataBanner';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtUsd = (n) => `$${(n || 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

const STATE_STYLE = {
  'verified':         { color: 'text-emerald-300', bg: 'bg-emerald-500/15 border-emerald-500/40', icon: CheckCircle2 },
  'approval-pending': { color: 'text-amber-300',   bg: 'bg-amber-500/15 border-amber-500/40',   icon: Clock },
  'approved':         { color: 'text-blue-300',    bg: 'bg-emerald-500/15 border-emerald-500/40',     icon: Clock },
  'executing':        { color: 'text-blue-300',    bg: 'bg-emerald-500/15 border-emerald-500/40',     icon: Clock },
  'rolled-back':      { color: 'text-red-300',     bg: 'bg-red-500/15 border-red-500/40',       icon: AlertOctagon },
  'rejected':         { color: 'text-slate-300',   bg: 'bg-slate-500/10 border-slate-500/30',  icon: AlertOctagon },
};

const Copilot = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/copilot/workflows`)
      .then((r) => { setData(r.data); setSelected(r.data.workflows[0]); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="copilot-page">
      <Helmet><title>FinOps Copilot | CARTIEAI</title></Helmet>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <DemoDataBanner />
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-orange-300 border border-orange-500/30 rounded-md px-2 py-1 mb-5" data-testid="cp-patent-badge">
            <FlaskConical className="w-3 h-3" /> Proprietary engine
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]"><BrandTitle>FinOps Copilot</BrandTitle></h1>
          <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed">
            Every other platform <span className="text-white font-semibold">stops at recommendations.</span> We orchestrate <span className="text-orange-300">allocate → propose → approve → execute → verify</span> across teams.
          </p>
          <p className="mt-3 text-sm text-slate-500">
            State-machine driven workflow. AI-generated exec summaries. Approver routing keyed by ($ threshold × service-tier). Full provenance of every decision.
          </p>
        </div>

        {loading && <Loader2 className="w-8 h-8 animate-spin mx-auto my-12 text-slate-500" />}

        {data && (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Card className="bg-slate-900/60 border border-white/10 shadow-sm border-emerald-500/30">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300">Verified savings</div>
                <div className="text-2xl font-bold text-white mt-1" data-testid="cp-verified">{fmtUsd(data.verified_savings_total_usd)}</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border border-white/10 shadow-sm border-amber-500/30">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-amber-300">Pending approval</div>
                <div className="text-2xl font-bold text-white mt-1" data-testid="cp-pending">{fmtUsd(data.pending_savings_total_usd)}</div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border border-white/10 shadow-sm border-white/10">
              <CardContent className="p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Workflows</div>
                <div className="text-2xl font-bold text-white mt-1">{data.count}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {data && (
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
            <Card className="lg:col-span-2 bg-slate-900/60 border border-white/10 shadow-sm border-white/10">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2"><WorkflowIcon className="w-5 h-5 text-orange-300" /> Workflows</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.workflows.map((w) => {
                  const style = STATE_STYLE[w.current_state] || STATE_STYLE['approved'];
                  const Icon = style.icon;
                  const isSel = selected?.id === w.id;
                  return (
                    <button key={w.id} onClick={() => setSelected(w)} className={`w-full text-left px-3 py-2.5 rounded border transition ${isSel ? style.bg + ' ' + (style.bg.includes('border') ? '' : 'border-white/30') : 'border-white/10 hover:bg-slate-950/60'}`} data-testid={`cp-wf-${w.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${style.color}`} />
                          <span className="text-[10px] font-mono uppercase text-slate-500">{w.id}</span>
                        </div>
                        <span className="text-xs font-mono text-white font-semibold">{fmtUsd(w.projected_savings_usd)}/mo</span>
                      </div>
                      <div className="text-sm text-white">{w.title}</div>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className={`text-[10px] font-semibold uppercase ${style.color}`}>{w.current_state}</span>
                        <span className="text-[10px] text-slate-500">→ {w.approver_role}</span>
                      </div>
                    </button>
                  );
                })}
              </CardContent>
            </Card>

            {selected && (
              <Card className="lg:col-span-3 bg-slate-900/60 border border-white/10 shadow-sm border-white/10" data-testid="cp-detail">
                <CardHeader>
                  <CardTitle className="text-white text-lg">{selected.title}</CardTitle>
                  <CardDescription>{selected.affected_service} · tier {selected.affected_tier}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-4 p-3 rounded border border-white/10 bg-slate-950">
                    <div className="text-[10px] uppercase tracking-widest text-orange-300 font-semibold mb-1">AI-generated executive summary</div>
                    <p className="text-xs text-slate-200 leading-relaxed">{selected.executive_summary}</p>
                  </div>
                  <div className="mb-4 flex items-center gap-2 text-xs">
                    <span className="text-slate-300">Routed to:</span>
                    <Badge variant="outline" className="border-orange-400/40 text-orange-200 font-mono">{selected.approver_role}</Badge>
                    <span className="text-slate-500 font-mono">via</span>
                    <Badge variant="outline" className="border-white/10 font-mono text-slate-300">{selected.approver_channel}</Badge>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Provenance trail</div>
                    <div className="space-y-1.5">
                      {selected.history.map((h, idx) => {
                        const style = STATE_STYLE[h.state] || STATE_STYLE['approved'];
                        return (
                          <div key={idx} className="flex items-start gap-2 text-xs">
                            <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${style.color.replace('text-', 'bg-')}`} />
                            <div className="flex-1">
                              <div className="text-slate-200"><span className={`font-mono ${style.color}`}>{h.state}</span> · by {h.by}</div>
                              {h.note && <div className="text-[10px] text-slate-500 mt-0.5">{h.note}</div>}
                            </div>
                            <span className="text-[10px] text-slate-300 font-mono flex-shrink-0">{new Date(h.ts).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {selected.verified_savings_usd !== null && selected.verified_savings_usd !== undefined && (
                    <div className="mt-4 p-3 rounded border border-emerald-500/30 bg-emerald-500/5">
                      <span className="text-[10px] uppercase tracking-widest text-emerald-300">Verified outcome</span>
                      <div className="text-white font-mono text-lg">{fmtUsd(selected.verified_savings_usd)}/mo realised</div>
                      <span className="text-xs text-slate-300">vs projected {fmtUsd(selected.projected_savings_usd)}/mo</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Copilot;
